import { Color } from "../../color";
import { math } from "../../math";
import { isFiniteNum } from "../../number";
import { ProcessedTextItem,ProcessedTextItem as _ProcessedTextItem } from "./processed-text-item";

const tmpV2A=new math.Vec2();
const tmpV2B=new math.Vec2();

export abstract class RenderingContext2D{
	public defaultTextStyle:RenderingContext2D.TextStyle&{fontSize:number}={
		vertAlign: 'middle',
		horzAlign: 'center',
		color: 'black',
		fontFamily: 'tahoma',
		fontWeight: 'normal',
		fontSize: 14,
		offset: [0,0],
	};

	public pxUnitFactor=1;//determins how big a pixel unit is, basically if you set it to two 1px is actual 2px useful for making things the same size no matter what devicePerPixel is
	public viewRotation=0;
	public readonly viewDownVecPx=new math.Vec2(0,1);
	public textTransformer=((text:string)=>[text]);

	public abstract get width():number;
	public abstract get height():number;

	public abstract save():void;
	public abstract restore():void;
	public abstract getTransform():DOMMatrix;
	public abstract setTransform(m:DOMMatrix):void;
	public abstract resetTransform():void;
	public abstract transform(a:number, b:number, c:number, d:number, e:number, f:number):void;
	public abstract translate(x:number, y:number):void;
	public abstract scale(x:number, y:number):void;
	public abstract rotate(angle:number):void;

	public abstract pxWidth(v?:number):number;
	public abstract pxUp():math.Vec2;

	public abstract get globalAlpha():number;
	public abstract set globalAlpha(v);

	public abstract get fillStyle():string|CanvasGradient|CanvasPattern;
	public abstract set fillStyle(v);

	public abstract setLineDash(segments:number[]):void;
	public setLineDashPx(segments:number[]){
		return this.setLineDash(segments.map(v=>this.pxWidth(v)));
	}

	public abstract get lineDashOffset():number;
	public abstract set lineDashOffset(v);

	public get lineDashOffsetPx(){
		return this.pxWidth(this.lineDashOffset);
	}
	public set lineDashOffsetPx(v){
		this.lineDashOffset=this.pxWidth(v);
	}

	public abstract get lineWidth():number;
	public abstract set lineWidth(v);

	public get lineWidthPx(){
		return this.lineWidth/this.pxWidth();
	}
	public set lineWidthPx(v){
		this.lineWidth=this.pxWidth(v);
	}

	public abstract get lineCap():CanvasLineCap;
	public abstract set lineCap(v);

	public abstract get lineJoin():CanvasLineJoin
	public abstract set lineJoin(v);

	public abstract get strokeStyle():string|RenderingContext2D.Gradient;
	public abstract set strokeStyle(v);

	public abstract beginPath():void;
	public abstract moveTo(x:number, y:number):void;
	public abstract lineTo(x:number, y:number):void;

	public pathTo(points:Iterable<math.Vec2Like>){
		points??=[];
		let i=0;
		for(const p of points){
			if(i===0)
				this.moveTo(p.x,p.y);
			else
				this.lineTo(p.x,p.y);
			i+=1;
		}
		// const it=points[Symbol.iterator]();
		// let r=it.next();
		// if(r.done)
		// 	return;
		// this.moveTo(r.value.x,r.value.y);
		// r=it.next();
		// while(!r.done){
		// 	this.lineTo(r.value.x,r.value.y);
		// 	r=it.next();
		// }
	}

	public abstract closePath():void;

	public abstract rect(x:number, y:number, w:number, h:number):void;

	public box(box:math.Box2){
		this.rect(box.min.x,box.min.y,box.size('x'),box.size('y'));
	}

	public abstract circle(x:number, y:number, radius:number):void;
	public circlePx(x:number, y:number, radius:number){
		return this.circle(x,y,this.pxWidth(radius));
	}

	public abstract ellipse(x:number, y:number, radiusX:number, radiusY:number, rotation:number, startAngle:number, endAngle:number, counterclockwise?:boolean):void;

	public abstract arc(x:number, y:number, radius:number, startAngle:number, endAngle:number, counterclockwise?:boolean):void;

	public abstract stroke(path?:Path2D):void;

	public abstract fill():void;
	public abstract fill(path:Path2D):void;
	public abstract fill(path?:Path2D):void;

	public abstract clip():void;
	public abstract clip(fillRule:CanvasFillRule):void;
	public abstract clip(fillRule?:CanvasFillRule):void;

	public abstract get font():string;
	public abstract set font(v);

	public abstract get textAlign():CanvasTextAlign;
	public abstract set textAlign(v);

	public abstract get textBaseline():CanvasTextBaseline
	public abstract set textBaseline(v);

	public abstract measureText(text:string):TextMetrics;

	public abstract strokeText(text:string, x:number, y:number, maxWidth?:number):void;

	public abstract fillText(text:string, x:number, y:number, maxWidth?:number):void;

	public abstract clearRect(x:number, y:number, w:number, h:number):void;

	public abstract fillRect(x:number, y:number, w:number, h:number):void;

	public abstract strokeRect(x:number, y:number, w:number, h:number):void;

	public createLinearGradient(){

	}
	public abstract drawImage(image:CanvasImageSource, dx:number, dy:number):void;
	public abstract drawImage(image:CanvasImageSource, dx:number, dy:number, dw:number, dh:number):void;
	public abstract drawImage(image:CanvasImageSource, sx:number, sy:number, sw:number, sh:number, dx:number, dy:number, dw:number, dh:number):void;
	public abstract drawImage(image:CanvasImageSource, sx:number, sy:number, sw?:number, sh?:number, dx?:number, dy?:number, dw?:number, dh?:number):void;

	private drawStroke(
		stroke:RenderingContext2D.StrokeStyle,
	){
		if(stroke){
			const {outline}=stroke;
			if(outline){
				const olw=outline.width*2;
				this.lineWidthPx=stroke.width+olw;
				this.strokeStyle=outline.color.toString();
				this.setLineDashPx(stroke.dash ?? []);
				this.lineDashOffsetPx=stroke.dashOffset ?? 0;
				this.stroke();
			}
			this.lineWidthPx=stroke.width;
			if(stroke.dash && stroke.dashColor){
				this.strokeStyle=stroke.dashColor.toString();
				this.setLineDash([]);
				this.stroke();
			}
			this.strokeStyle=stroke.color.toString();
			this.setLineDashPx(stroke.dash ?? []);
			this.lineDashOffsetPx=stroke.dashOffset ?? 0;
			this.stroke();
		}
	}

	public drawLineString(
		points:Iterable<math.Vec2Like>,
		stroke:RenderingContext2D.StrokeStyle,
	){
		this.lineCap='round';
		this.beginPath();
		this.pathTo(points);

		this.drawStroke(stroke);
	}

	public drawLineStrings(
		points:Iterable<Iterable<math.Vec2Like>>,
		stroke:RenderingContext2D.StrokeStyle,
	){
		this.lineCap='round';
		this.beginPath();
		for(const item of points)
			this.pathTo(item);

		this.drawStroke(stroke);
	}

	public drawArrow(
		pointA:math.Vec2Like,
		pointB:math.Vec2Like,
		stroke:RenderingContext2D.StrokeStyle,
	){
		this.lineCap='round';
		this.beginPath();
		this.pathTo([pointA,pointB]);
		const arrowHeadSize=this.pxWidth(stroke.width*math.SQRT2);
		const dir=new math.Vec2().copy(pointB).sub(pointA).normalize();
		const nor=dir.clone().rotate90();
		const p=dir.clone().multiplyScalar(-arrowHeadSize).add(pointB);
		p.addScaledVector(nor,arrowHeadSize);
		const q=p.clone().addScaledVector(nor,-arrowHeadSize*2);
		this.pathTo([p,pointB,q]);
		this.drawStroke(stroke);
	}

	public drawPolygon(
		points:Iterable<math.Vec2Like>,
		fill:string|Color|null,
		stroke:RenderingContext2D.StrokeStyle,
		notFill:string|Color|null=null,
	){
		this.lineCap='round';
		this.lineJoin='round';

		if(notFill){
			points=[...points];
			this.save();
			this.beginPath();
			this.moveTo(-1000,-1000);
			this.lineTo(+1000,-1000);
			this.lineTo(+1000,+1000);
			this.lineTo(-1000,+1000);
			this.closePath();
			this.pathTo(points);
			this.closePath();
			this.clip('evenodd');

			this.beginPath();
			this.moveTo(-1000,+1000);
			this.lineTo(+1000,+1000);
			this.lineTo(+1000,-1000);
			this.lineTo(-1000,-1000);
			this.closePath();

			this.fillStyle=notFill.toString();
			this.fill();
			this.restore();
		}

		this.beginPath();
		this.pathTo(points);
		this.closePath();

		if(fill){
			this.fillStyle=fill.toString();
			this.fill();
		}
		this.drawStroke(stroke);
	}

	public drawCircle(
		center:math.Vec2Like,
		radius:number,
		fill:string,
		stroke:RenderingContext2D.StrokeStyle,
	){
		this.lineCap='round';
		this.lineJoin='round';
		this.beginPath();
		this.circle(center.x,center.y,radius);

		if(fill){
			this.fillStyle=fill;
			this.fill();
		}

		this.drawStroke(stroke);
	}

	public drawPoints<Point extends math.Vec2Like>(
		positions:Iterable<math.Vec2Like>,
		style:RenderingContext2D.PointStyle|((p:Point,i:number)=>RenderingContext2D.PointStyle),
	):void;
	public drawPoints(
		positions:Iterable<math.Vec2Like>,
		style:RenderingContext2D.PointStyle|((p:math.Vec2Like,i:number)=>RenderingContext2D.PointStyle),
	){
		if(typeof(style)==='function'){
			const getStyle=style;
			let i=0;
			for(const p of positions){
				const style=getStyle(p,i);
				let {radius,outline}=style;
				this.fillStyle=style.color.toString();
				if(outline){
					this.strokeStyle=outline.color.toString();
					this.lineWidthPx=outline.width*2;
				}
				radius=this.pxWidth(radius);

				this.beginPath();
				this.circle(p.x,p.y,radius);
				if(outline)
					this.stroke();
				this.fill();
				i+=1;
			}
		}else{
			let {radius,outline}=style;
			this.fillStyle=style.color.toString();
			if(outline){
				this.strokeStyle=outline.color.toString();
				this.lineWidthPx=outline.width*2;
			}
			radius=this.pxWidth(radius);
			for(const p of positions){
				this.beginPath();
				this.circle(p.x,p.y,radius);
				if(outline)
					this.stroke();
				this.fill();
			}
		}
	}

	private symbolAnchorToTransform(
		coordToPixel:DOMMatrix,
		pos:math.ReadonlyVec2|math.ReadonlyVec3,
		_dir:math.ReadonlyVec2|math.ReadonlyVec3|null,
	){
		let rotation=this.viewRotation;
		if(_dir){
			const dir=tmpV2B.copy(pos).add(_dir);
			pos=tmpV2A.copy(coordToPixel.transformPoint(pos));
			dir.copy(coordToPixel.transformPoint(dir));
			dir.sub(pos);
			rotation=Math.atan2(dir.y,dir.x)+math.PIHALF;
		}else{
			pos=tmpV2A.copy(coordToPixel.transformPoint(pos));
		}
		return <const>[pos,rotation];
	}

	public drawSymbol(
		pos:math.ReadonlyVec2|math.ReadonlyVec3,
		dir:math.ReadonlyVec2|math.ReadonlyVec3|null,
		paths:Path2D[],
		style:RenderingContext2D.SymbolStyle,
	){
		this.drawSymbols([{pos,dir}],paths,style);
	}

	public drawSymbols(
		items:{
			pos:math.ReadonlyVec2|math.ReadonlyVec3,
			dir?:math.ReadonlyVec2|math.ReadonlyVec3|null,
		}[],
		paths:Path2D[],
		style:RenderingContext2D.SymbolStyle,
	){
		this.save();
		const coordToPixel=this.getTransform();
		this.resetTransform();

		this.fillStyle=style.color.toString();
		const {outline}=style;

		const scale=style.radius*2;
		if(outline){
			this.strokeStyle=outline.color.toString();
			this.lineWidth=outline.width*2/scale;
			this.setLineDash([]);
		}

		for(const item of items){
			const [pos,rot]=this.symbolAnchorToTransform(coordToPixel,item.pos,item.dir ?? null);
			this.save();
			this.translate(pos.x,pos.y);
			this.rotate(rot);
			if(style.offset){
				this.translate(style.offset[0],style.offset[1]);
			}
			this.scale(scale,scale);
			for(const path of paths){
				if(outline)
					this.stroke(path);
				this.fill(path);
			}
			this.restore();
		}
		this.restore();

	}

	public drawImageSymbol(
		position:math.Vec2Like,
		rotation:number,
		image:HTMLImageElement,
		style:{radius:number},
	){
		if(!(image.complete && image.naturalWidth>0))
			return;
		this.save();

		const pxWidth=this.pxWidth(style.radius*2);
		const scaleX=pxWidth/image.naturalWidth;
		const scaleY=pxWidth/image.naturalHeight;
		const up=this.pxUp();

		this.translate(position.x,position.y);
		if(up.y>0)
			this.scale(scaleX,-scaleY);
		else
			this.scale(scaleX,scaleY);
		rotation??=this.viewRotation;
		this.rotate(rotation);
		this.translate(-image.naturalWidth/2,-image.naturalHeight/2)
		this.drawImage(image,0,0);

		this.restore();
	}

	public abstract processText(
		items:RenderingContext2D.TextItem[],
		style?:RenderingContext2D.TextStyle,
	):ProcessedTextItem[];

	public abstract drawProcessedText(
		items:ProcessedTextItem[],
	):void;

	public drawText(
		items:RenderingContext2D.TextItem[],
		style:RenderingContext2D.TextStyle={},
	){
		const _items=this.processText(items,style);
		this.drawProcessedText(_items);

		// return ()=>{
		// 	const pixelToCoord=coordToPixel.inverse();
		// 	return _items.map(item=>{
		// 		return new math.Polygon2([...item.outerBox.corners()].map(p=>math.Vec2.from(pixelToCoord.transformPoint(p.add(item.position)))));
		// 	})
		// };
	}

	public styleToFontSize(
		style:RenderingContext2D.TextStyle,
	):{size:number,unit:'m'|'px'}{
		let size=style.fontSize;
		let unit:'m'|'px'='px';
		if(typeof(size)==='string'){
			if(size.endsWith('%')){
				size=(+size.substring(0,size.length-1))*this.defaultTextStyle.fontSize/100;
				unit='px';
			}else if(size.endsWith('m')){
				size=(+size.substring(0,size.length-1));
				unit='m';
			}else if(size.endsWith('px')){
				size=(+size.substring(0,size.length-2));
				unit='px';
			}
		}
		if(!isFiniteNum(size)){
			size=this.defaultTextStyle.fontSize;
			unit='px';
		}
		return {size,unit};
	}
}

export namespace RenderingContext2D{
	export interface StrokeStyle{
		color:string|Color|Map<number,Color>;
		width:number;
		dash?:number[];
		dashOffset?:number;
		dashColor?:string|Color;
		outline?:{
			color:string|Color,
			width:number,
		};
	}

	export interface PointStyle{
		color:string|Color;
		radius:number;
		outline?:{
			color:string|Color,
			width:number,
		};
	}

	export interface SymbolStyle{
		color:string|Color;
		radius:number;
		offset?:[number,number];
		outline?:{
			color:string|Color,
			width:number,
		};
	}


	export interface TextStyle{
		vertAlign?:'middle'|'bottom'|'top';//'baseline';
		offset?:[number,number];
		horzAlign?:CanvasTextAlign;
		color?:string|Color;
		outline?:{
			color:string|Color,
			width:number
		};
		background?:{
			color:string|Color;
			padding:[number,number,number,number];
			outline?:{
				color:string,
				width:number
			};
		},
		fontFamily?:string;
		fontWeight?:'normal'|'bold';
		fontSize?:number|string;
	}

	export type TextItem={
		text:string;
		anchor?:math.ReadonlyVec2|math.ReadonlyVec3|math.Line2|math.LineString<math.Vec2|math.Vec3>|math.Polygon<math.Vec2|math.Vec3>;
	};

	export type ProcessedTextItem=_ProcessedTextItem;

	export class Gradient{
		public constructor(
			public readonly a:math.ReadonlyVec2,
			public readonly b:math.ReadonlyVec2,
			public readonly stops:(readonly [number,Color])[],
		){
		}
	}
}
